import React, { useState } from 'react';
import { ChevronRight, TrendingUp, TrendingDown, Package, X, Download, AlertTriangle } from 'lucide-react';
import { AppBadge } from '../components/base/AppBadge';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { PeriodSelector } from '../components/PeriodSelector';
type CostTab = 'provider' | 'service' | 'budget';
interface ProviderCost {
  id: string;
  name: string;
  costMonth: number;
  trend: number;
  isTopSpender?: boolean;
  isTopSaver?: boolean;
}
interface ServiceCost {
  id: string;
  name: string;
  vendor: string;
  costMonth: number;
  efficiencyGrade: 'A' | 'B' | 'C' | 'D';
  isTopSpender?: boolean;
  isTopSaver?: boolean;
}
interface BudgetLine {
  id: string;
  name: string;
  spent: number;
  budget: number;
  percent: number;
}
interface Alert {
  id: string;
  severity: 'amber' | 'red' | 'blue';
  service: string;
  provider?: string;
  vendor?: string;
  metric: string;
  metricLabel: string;
  message: string;
}
const PROVIDER_DATA: ProviderCost[] = [{
  id: 'p1',
  name: 'OpenAI',
  costMonth: 1580,
  trend: 8,
  isTopSpender: true
}, {
  id: 'p2',
  name: 'Anthropic',
  costMonth: 520,
  trend: 12
}, {
  id: 'p3',
  name: 'Google Gemini',
  costMonth: 240,
  trend: -3,
  isTopSaver: true
}];
const SERVICE_DATA: ServiceCost[] = [{
  id: 's1',
  name: 'Customer Assistant',
  vendor: 'Dmood',
  costMonth: 680,
  efficiencyGrade: 'B'
}, {
  id: 's2',
  name: 'Smart Matcher',
  vendor: 'Acme Dev',
  costMonth: 420,
  efficiencyGrade: 'A',
  isTopSaver: true
}, {
  id: 's3',
  name: 'Content Moderator',
  vendor: 'Internal',
  costMonth: 340,
  efficiencyGrade: 'D',
  isTopSpender: true
}, {
  id: 's4',
  name: 'AI Tutor',
  vendor: 'Dmood',
  costMonth: 240,
  efficiencyGrade: 'A'
}];
const BUDGET_DATA: BudgetLine[] = [{
  id: 'b1',
  name: 'Customer Care - Interno',
  spent: 1200,
  budget: 1500,
  percent: 80
}, {
  id: 'b2',
  name: 'Marketing - Fornitore Acme',
  spent: 1450,
  budget: 1400,
  percent: 104
}, {
  id: 'b3',
  name: 'HR & Training - Interno',
  spent: 680,
  budget: 1000,
  percent: 68
}];
const ALERTS: Alert[] = [{
  id: 'a1',
  severity: 'amber',
  service: 'Content Moderator',
  provider: 'OpenAI',
  vendor: 'Internal',
  metric: '+34%',
  metricLabel: 'Aumento costi',
  message: 'Incremento significativo dei costi questa settimana'
}, {
  id: 'a2',
  severity: 'red',
  service: 'Marketing',
  vendor: 'Acme',
  metric: '€1.450 / €1.400',
  metricLabel: 'Budget superato',
  message: 'Linea di budget superata del 4%'
}, {
  id: 'a3',
  severity: 'blue',
  service: 'Smart Matcher',
  provider: 'Anthropic',
  vendor: 'Acme Dev',
  metric: '3x',
  metricLabel: 'Costo GPT-4 Turbo vs GPT-4o',
  message: 'Opportunità di ottimizzazione: valutare switch a GPT-4o per lo stesso task'
}];
export function CostManagementPage() {
  const [activeTab, setActiveTab] = useState<CostTab>('provider');
  const [isAlertDrawerOpen, setIsAlertDrawerOpen] = useState(false);
  const getBarColor = (percent: number) => {
    if (percent > 100) return 'bg-red-500';
    if (percent >= 80) return 'bg-amber-500';
    return 'bg-[#8eff01]';
  };
  const getDotColor = (severity: Alert['severity']) => {
    if (severity === 'red') return 'bg-red-500';
    if (severity === 'amber') return 'bg-amber-500';
    return 'bg-blue-500';
  };
  const getProviderIcon = (providerName: string) => {
    switch (providerName) {
      case 'OpenAI':
        return <img src="https://cdn.magicpatterns.com/uploads/1uRgHdJpNKcdeM7NG8hvjt/image.png" alt="OpenAI" className="w-6 h-6 object-contain" data-id="element-2281" />;
      case 'Anthropic':
        return <img src="https://cdn.magicpatterns.com/uploads/aLJFmhZ7nGDmPeNzEd4tUL/anthropic-1.svg" alt="Anthropic" className="w-6 h-6 object-contain" data-id="element-2282" />;
      case 'Google Gemini':
        return <img src="https://cdn.magicpatterns.com/uploads/6ACcFT2ZyoV6cbNrxRM4jT/Google_Gemini_icon_2025.svg.png" alt="Google Gemini" className="w-6 h-6 object-contain" data-id="element-2283" />;
      default:
        return <Package className="w-6 h-6 text-white/70" data-id="element-2284" />;
    }
  };
  return <div className="min-h-screen bg-[#121212]" data-id="element-2285">
      {/* Header Section */}
      <div className="pt-6 px-4 md:px-8 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2286">
        <h1 className="font-semibold text-white tracking-tight text-[20px]" data-id="element-2287">
          Cost Management
        </h1>

        <div className="flex items-center gap-3" data-id="element-2288">
          <PeriodSelector data-id="element-2289" />

          <AppButton variant="ghost" size="sm" title="Export CSV" className="px-2" data-id="element-2290">
            <Download className="w-4 h-4" data-id="element-2291" />
          </AppButton>

          {ALERTS.length > 0 && <AppButton variant="secondary" size="sm" onClick={() => setIsAlertDrawerOpen(true)} className="relative flex items-center gap-2 rounded-full" data-id="element-2292">
              <AlertTriangle className="w-4 h-4 text-white/70" data-id="element-2293" />
              <span className="text-[13px] font-medium text-white hidden sm:inline" data-id="element-2294">
                Segnalazioni
              </span>
              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full text-[#FFFFFF] bg-[#FF0026]" data-id="element-2295">
                {ALERTS.length}
              </span>
            </AppButton>}
        </div>
      </div>

      {/* Main Content Container */}
      <div className="px-4 md:px-8 pb-12 flex flex-col gap-8" data-id="element-2296">
        {/* KPI Cards Section - Minimal */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2297">
          <AppCard padding="normal" data-id="element-2298">
            <p className="font-bold text-white text-[32px] leading-none mb-2" data-id="element-2299">
              €2.340
            </p>
            <p className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-2300">
              Spesa Totale
            </p>
          </AppCard>

          <AppCard padding="normal" data-id="element-2301">
            <p className="font-bold text-white text-[32px] leading-none mb-2" data-id="element-2302">
              3/10
            </p>
            <p className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-2303">
              Provider
            </p>
          </AppCard>

          <AppCard padding="normal" data-id="element-2304">
            <p className="font-bold text-white text-[32px] leading-none mb-2" data-id="element-2305">
              8/16
            </p>
            <p className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-2306">
              Servizi
            </p>
          </AppCard>

          <AppCard padding="normal" data-id="element-2307">
            <p className="font-bold text-white text-[32px] leading-none mb-2" data-id="element-2308">
              {BUDGET_DATA.length}
            </p>
            <p className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-2309">
              Linee Budget
            </p>
          </AppCard>
        </div>

        {/* Insights Section - Simplified */}
        <AppCard padding="normal" data-id="element-2310">
          <h2 className="text-[13px] font-semibold text-white/60 mb-6 uppercase tracking-wider" data-id="element-2311">
            Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2312">
            <div data-id="element-2313">
              <p className="text-[28px] font-bold text-[#8eff01] mb-1" data-id="element-2314">
                €126/mese
              </p>
              <p className="text-[12px] text-white/60" data-id="element-2315">
                Risparmio potenziale con GPT-4o
              </p>
            </div>
            <div data-id="element-2316">
              <p className="text-[28px] font-bold text-amber-400 mb-1" data-id="element-2317">+34%</p>
              <p className="text-[12px] text-white/60" data-id="element-2318">
                Aumento costi OpenAI questa settimana
              </p>
            </div>
            <div data-id="element-2319">
              <p className="text-[28px] font-bold text-white mb-1" data-id="element-2320">40%</p>
              <p className="text-[12px] text-white/60" data-id="element-2321">
                Capacità disponibile sotto budget
              </p>
            </div>
          </div>
        </AppCard>

        {/* Detail Section */}
        <div className="flex flex-col gap-6" data-id="element-2322">
          {/* Tab Navigation */}
          <div className="flex gap-8 border-b border-white/[0.06]" data-id="element-2323">
            <button onClick={() => setActiveTab('provider')} className={`relative px-1 pb-3 text-[13px] font-medium transition-all whitespace-nowrap ${activeTab === 'provider' ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-2324">
              Per Provider
              {activeTab === 'provider' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2325" />}
            </button>
            <button onClick={() => setActiveTab('service')} className={`relative px-1 pb-3 text-[13px] font-medium transition-all whitespace-nowrap ${activeTab === 'service' ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-2326">
              Per Servizio
              {activeTab === 'service' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2327" />}
            </button>
            <button onClick={() => setActiveTab('budget')} className={`relative px-1 pb-3 text-[13px] font-medium transition-all whitespace-nowrap ${activeTab === 'budget' ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-2328">
              Linee di Budget
              {activeTab === 'budget' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2329" />}
            </button>
          </div>

          {/* Tab Content */}
          <AppCard padding="none" className="overflow-hidden" data-id="element-2330">
            {activeTab === 'provider' && <div className="overflow-x-auto" data-id="element-2331">
                <table className="w-full" data-id="element-2332">
                  <thead className="bg-white/[0.02] border-b border-white/[0.06]" data-id="element-2333">
                    <tr data-id="element-2334">
                      <th className="px-6 py-4 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-2335">
                        Provider
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-2336">
                        Spesa
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-2337">
                        Trend
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]" data-id="element-2338">
                    {PROVIDER_DATA.map(provider => <tr key={provider.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-2339">
                        <td className="px-6 py-5" data-id="element-2340">
                          <div className="flex items-center gap-3" data-id="element-2341">
                            <div className="flex-shrink-0" data-id="element-2342">
                              {getProviderIcon(provider.name)}
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2343">
                              <span className="text-[13px] font-medium text-white" data-id="element-2344">
                                {provider.name}
                              </span>
                              {provider.isTopSpender && <AppBadge variant="amber" size="sm" data-id="element-2345">
                                  Top Spender
                                </AppBadge>}
                              {provider.isTopSaver && <AppBadge variant="green" size="sm" data-id="element-2346">
                                  Top Saver
                                </AppBadge>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-[13px] font-medium text-white" data-id="element-2347">
                          €{provider.costMonth.toLocaleString()}
                        </td>
                        <td className="px-6 py-5" data-id="element-2348">
                          <div className={`flex items-center text-[13px] font-medium ${provider.trend > 0 ? 'text-amber-400' : 'text-green-400'}`} data-id="element-2349">
                            {provider.trend > 0 ? <TrendingUp className="w-4 h-4 mr-1" data-id="element-2350" /> : <TrendingDown className="w-4 h-4 mr-1" data-id="element-2351" />}
                            {provider.trend > 0 ? '+' : ''}
                            {provider.trend}%
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {activeTab === 'service' && <div className="overflow-x-auto" data-id="element-2352">
                <table className="w-full" data-id="element-2353">
                  <thead className="bg-white/[0.02] border-b border-white/[0.06]" data-id="element-2354">
                    <tr data-id="element-2355">
                      <th className="px-6 py-4 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-2356">
                        Servizio
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-2357">
                        Costo
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-2358">
                        Efficienza
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]" data-id="element-2359">
                    {SERVICE_DATA.map(service => <tr key={service.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-2360">
                        <td className="px-6 py-5" data-id="element-2361">
                          <div className="flex items-center gap-2" data-id="element-2362">
                            <div data-id="element-2363">
                              <p className="text-[13px] font-medium text-white" data-id="element-2364">
                                {service.name}
                              </p>
                              <p className="text-[11px] text-white/40 mt-0.5" data-id="element-2365">
                                {service.vendor}
                              </p>
                            </div>
                            {service.isTopSpender && <AppBadge variant="amber" size="sm" data-id="element-2366">
                                Top Spender
                              </AppBadge>}
                            {service.isTopSaver && <AppBadge variant="green" size="sm" data-id="element-2367">
                                Top Saver
                              </AppBadge>}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-[13px] font-medium text-white" data-id="element-2368">
                          €{service.costMonth}
                        </td>
                        <td className="px-6 py-5" data-id="element-2369">
                          <AppBadge variant={service.efficiencyGrade === 'A' ? 'green' : service.efficiencyGrade === 'B' ? 'blue' : service.efficiencyGrade === 'C' ? 'amber' : 'red'} data-id="element-2370">
                            {service.efficiencyGrade}
                          </AppBadge>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {activeTab === 'budget' && <div className="p-8 space-y-6" data-id="element-2371">
                {BUDGET_DATA.map(line => <div key={line.id} className="space-y-3" data-id="element-2372">
                    <div className="flex items-center justify-between" data-id="element-2373">
                      <p className="text-[13px] font-medium text-white" data-id="element-2374">
                        {line.name}
                      </p>
                      <div className="flex items-center gap-4" data-id="element-2375">
                        <span className="text-[12px] text-white/40" data-id="element-2376">
                          €{line.spent.toLocaleString()} / €
                          {line.budget.toLocaleString()}
                        </span>
                        <span className={`text-[14px] font-semibold ${line.percent > 100 ? 'text-red-500' : line.percent >= 80 ? 'text-amber-500' : 'text-[#8eff01]'}`} data-id="element-2377">
                          {line.percent}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden" data-id="element-2378">
                      <div className={`h-full transition-all ${getBarColor(line.percent)}`} style={{
                  width: `${Math.min(line.percent, 100)}%`
                }} data-id="element-2379" />
                    </div>
                  </div>)}
              </div>}
          </AppCard>
        </div>
      </div>

      {/* Alert Drawer */}
      <>
        {/* Backdrop */}
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ease-in-out ${isAlertDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsAlertDrawerOpen(false)} data-id="element-2380" />

        {/* Drawer */}
        <div className={`fixed top-0 right-0 h-full w-full md:w-[480px] bg-[#121212] z-50 shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-in-out ${isAlertDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2381">
          {/* Drawer Header */}
          <div className="sticky top-0 bg-[#121212] border-b border-white/[0.06] px-6 py-5 flex items-center justify-between" data-id="element-2382">
            <div data-id="element-2383">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-2384">
                Alert & Notifiche
              </h2>
              <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2385">
                {ALERTS.length} notifiche attive
              </p>
            </div>
            <button onClick={() => setIsAlertDrawerOpen(false)} className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-all" data-id="element-2386">
              <X className="w-5 h-5" data-id="element-2387" />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="p-6 space-y-3" data-id="element-2388">
            {ALERTS.map(alert => <div key={alert.id} className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg p-4" data-id="element-2389">
                <div className="flex items-start gap-3" data-id="element-2390">
                  <div className={`w-1.5 h-1.5 rounded-full ${getDotColor(alert.severity)} mt-1.5 flex-shrink-0`} data-id="element-2391" />
                  <div className="flex-1 space-y-2" data-id="element-2392">
                    {/* Header: Service + Provider/Vendor */}
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-2393">
                      <span className="text-[13px] font-semibold text-white" data-id="element-2394">
                        {alert.service}
                      </span>
                      {alert.provider && <>
                          <span className="text-white/20" data-id="element-2395">·</span>
                          <span className="text-[11px] text-white/40" data-id="element-2396">
                            {alert.provider}
                          </span>
                        </>}
                      {alert.vendor && <>
                          <span className="text-white/20" data-id="element-2397">·</span>
                          <span className="text-[11px] text-white/40" data-id="element-2398">
                            {alert.vendor}
                          </span>
                        </>}
                    </div>

                    {/* Metric */}
                    <div className="flex items-baseline gap-1.5" data-id="element-2399">
                      <span className="text-[16px] font-bold text-white" data-id="element-2400">
                        {alert.metric}
                      </span>
                      <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-2401">
                        {alert.metricLabel}
                      </span>
                    </div>

                    {/* Message */}
                    <p className="text-[12px] text-white/40 leading-relaxed" data-id="element-2402">
                      {alert.message}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </>
    </div>;
}